import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { db } from './firebase';
import { collectionGroup, deleteDoc, getDocs, query, where } from 'firebase/firestore';

const CLIENT_IDS = ['2ejjeh', 'lucea'];

function normalizeKeyPart(val) {
  if (val == null) return '';
  return String(val).trim();
}

function duplicateGroupKey(recipientName, recipientNumber, importDateKey) {
  return [
    normalizeKeyPart(recipientName),
    normalizeKeyPart(recipientNumber),
    normalizeKeyPart(importDateKey)
  ].join('\u0001');
}

function rowSelectionKey(groupKey, rowId) {
  return `${groupKey}\u0002${rowId}`;
}

function DuplicatedPage({ userRole, userClientId }) {
  const defaultClient =
    userRole === 'admin'
      ? '2ejjeh'
      : CLIENT_IDS.includes(userClientId)
        ? userClientId
        : '2ejjeh';

  const [selectedClient, setSelectedClient] = useState(defaultClient);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [duplicateGroups, setDuplicateGroups] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);

  const canPickClient = userRole === 'admin';

  useEffect(() => {
    if (!canPickClient && userClientId) {
      setSelectedClient(userClientId);
    }
  }, [canPickClient, userClientId]);

  const loadDuplicates = useCallback(async () => {
    setLoading(true);
    setErrorText('');
    setDuplicateGroups(null);
    setSelectedRowKeys(new Set());

    try {
      const itemsQuery = query(
        collectionGroup(db, 'items'),
        where('clientId', '==', selectedClient)
      );
      const snapshot = await getDocs(itemsQuery);

      const byKey = new Map();

      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const key = duplicateGroupKey(
          data.recipientName,
          data.recipientNumber,
          data.importDateKey
        );
        if (!byKey.has(key)) {
          byKey.set(key, []);
        }
        byKey.get(key).push({
          ...data,
          id: docSnap.id,
          itemRef: docSnap.ref
        });
      });

      const groups = [];
      byKey.forEach((items, key) => {
        if (items.length > 1) {
          const [name, number, dateKey] = key.split('\u0001');
          groups.push({
            key,
            recipientName: name,
            recipientNumber: number,
            importDateKey: dateKey,
            items
          });
        }
      });

      groups.sort((a, b) => {
        const da = String(a.importDateKey);
        const db_ = String(b.importDateKey);
        if (da !== db_) return db_.localeCompare(da);
        return String(a.recipientName).localeCompare(String(b.recipientName));
      });

      setDuplicateGroups(groups);
    } catch (e) {
      console.error(e);
      setErrorText(e?.message || 'Failed to load items');
    } finally {
      setLoading(false);
    }
  }, [selectedClient]);

  const totalDuplicateRows = useMemo(() => {
    if (!duplicateGroups?.length) return 0;
    return duplicateGroups.reduce((sum, g) => sum + g.items.length, 0);
  }, [duplicateGroups]);

  const selectedCount = selectedRowKeys.size;

  const toggleRowSelected = useCallback((groupKey, rowId) => {
    const key = rowSelectionKey(groupKey, rowId);
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleDeleteSelected = useCallback(async () => {
    if (!duplicateGroups?.length || selectedRowKeys.size === 0) return;

    const entries = [];
    duplicateGroups.forEach((g) => {
      g.items.forEach((row) => {
        const sk = rowSelectionKey(g.key, row.id);
        if (selectedRowKeys.has(sk) && row.itemRef) {
          entries.push({ sk, groupKey: g.key, row });
        }
      });
    });

    if (entries.length === 0) {
      setErrorText('No valid rows selected (missing references).');
      return;
    }

    if (
      !window.confirm(
        `Delete ${entries.length} selected duplicate row(s)? This cannot be undone.`
      )
    ) {
      return;
    }

    setErrorText('');
    setDeleting(true);
    try {
      const results = await Promise.allSettled(
        entries.map((e) => deleteDoc(e.row.itemRef))
      );

      const succeeded = [];
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') succeeded.push(entries[i]);
        else console.error('Delete failed:', entries[i].row.id, r.reason);
      });

      if (succeeded.length === 0) {
        setErrorText('Failed to delete selected items.');
        return;
      }

      const removeByGroup = new Map();
      succeeded.forEach((e) => {
        if (!removeByGroup.has(e.groupKey)) removeByGroup.set(e.groupKey, new Set());
        removeByGroup.get(e.groupKey).add(e.row.id);
      });

      setDuplicateGroups((prev) => {
        if (!prev) return prev;
        return prev
          .map((g) => {
            const ids = removeByGroup.get(g.key);
            if (!ids) return g;
            return { ...g, items: g.items.filter((i) => !ids.has(i.id)) };
          })
          .filter((g) => g.items.length > 1);
      });

      setSelectedRowKeys((prev) => {
        const next = new Set(prev);
        succeeded.forEach((e) => next.delete(e.sk));
        return next;
      });

      const failed = entries.length - succeeded.length;
      if (failed > 0) {
        setErrorText(
          `Deleted ${succeeded.length} item(s). ${failed} could not be deleted.`
        );
      }
    } catch (e) {
      console.error(e);
      setErrorText(e?.message || 'Failed to delete selected items');
    } finally {
      setDeleting(false);
    }
  }, [duplicateGroups, selectedRowKeys]);

  return (
    <div
      style={{
        padding: '32px',
        maxWidth: '1200px',
        margin: '0 auto',
        backgroundColor: '#f8f9fa',
        minHeight: '100vh'
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '32px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <h1
          style={{
            margin: '0 0 8px 0',
            fontSize: '28px',
            color: '#2c3e50',
            fontWeight: '600'
          }}
        >
          Duplicated orders
        </h1>
        <p style={{ margin: '0 0 24px 0', color: '#6c757d', fontSize: '16px' }}>
          Orders that share the same recipient name, recipient number, and import date key
          (multiple rows in <code>imports/…/items</code>).
        </p>

        <div style={{ marginBottom: '20px' }}>
          <span
            style={{
              display: 'block',
              marginBottom: '10px',
              color: '#495057',
              fontWeight: '600',
              fontSize: '16px'
            }}
          >
            Client
          </span>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
            {canPickClient ? (
              CLIENT_IDS.map((id) => (
                <label
                  key={id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    color: '#495057',
                    fontSize: '15px'
                  }}
                >
                  <input
                    type="radio"
                    name="duplicated-client"
                    value={id}
                    checked={selectedClient === id}
                    onChange={() => setSelectedClient(id)}
                  />
                  {id}
                </label>
              ))
            ) : (
              <span style={{ fontSize: '15px', color: '#495057' }}>
                <strong>{userClientId || selectedClient}</strong>
                <span style={{ marginLeft: '8px', fontSize: '14px', color: '#1976d2' }}>
                  (your assigned client)
                </span>
              </span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={loadDuplicates}
          disabled={loading}
          style={{
            backgroundColor: loading ? '#6c757d' : '#6f42c1',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Loading…' : 'Scan for duplicates'}
        </button>
      </div>

      {errorText ? (
        <div
          style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '24px',
            border: '1px solid #f5c6cb'
          }}
        >
          {errorText}
        </div>
      ) : null}

      {duplicateGroups && (
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', color: '#2c3e50' }}>
            Results for <strong>{selectedClient}</strong>
          </h2>
          <p style={{ margin: '0 0 20px 0', color: '#6c757d' }}>
            {duplicateGroups.length} duplicate group(s), {totalDuplicateRows} total matching rows
            (each group has at least 2 rows).
          </p>

          {duplicateGroups.length === 0 ? (
            <p style={{ color: '#155724', margin: 0 }}>No duplicates found for this client.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}
              >
                <span style={{ color: '#495057', fontSize: '14px' }}>
                  <strong>{selectedCount}</strong> row(s) selected
                </span>
                <button
                  type="button"
                  onClick={handleDeleteSelected}
                  disabled={selectedCount === 0 || deleting}
                  style={{
                    padding: '8px 18px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'white',
                    backgroundColor:
                      selectedCount === 0 || deleting ? '#adb5bd' : '#dc3545',
                    border: 'none',
                    borderRadius: '6px',
                    cursor:
                      selectedCount === 0 || deleting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {deleting ? 'Deleting…' : 'Delete selected'}
                </button>
              </div>
              {duplicateGroups.map((group) => (
                <div
                  key={group.key}
                  style={{
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      backgroundColor: '#e7e3f0',
                      padding: '12px 16px',
                      borderBottom: '1px solid #dee2e6',
                      fontSize: '14px',
                      color: '#4a148c'
                    }}
                  >
                    <strong>Import date key:</strong> {group.importDateKey || '(empty)'} ·{' '}
                    <strong>Name:</strong> {group.recipientName || '(empty)'} ·{' '}
                    <strong>Number:</strong> {group.recipientNumber || '(empty)'} ·{' '}
                    <strong>Count:</strong> {group.items.length}
                    {(() => {
                      const statuses = [
                        ...new Set(
                          group.items.map((i) =>
                            i.status != null && String(i.status).trim() !== ''
                              ? String(i.status).trim()
                              : null
                          )
                        )
                      ].filter(Boolean);
                      return statuses.length ? (
                        <>
                          {' '}
                          · <strong>Statuses:</strong> {statuses.join(', ')}
                        </>
                      ) : null;
                    })()}
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '13px'
                      }}
                    >
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                          {[
                            '',
                            'Document ID',
                            'Status',
                            'Waybill',
                            'Area',
                            'Directions',
                            'COD',
                            'Qty',
                            'Notes'
                          ].map((h, idx) => (
                            <th
                              key={idx}
                              style={{
                                textAlign: idx === 0 ? 'center' : 'left',
                                padding: '10px 12px',
                                borderBottom: '1px solid #dee2e6',
                                fontWeight: '600',
                                color: '#495057',
                                whiteSpace: 'nowrap',
                                width: idx === 0 ? '44px' : undefined
                              }}
                            >
                              {h === '' ? 'Select' : h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {group.items.map((row) => {
                          const sk = rowSelectionKey(group.key, row.id);
                          return (
                          <tr key={`${group.key}-${row.id}`}>
                            <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', verticalAlign: 'middle', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={selectedRowKeys.has(sk)}
                                onChange={() => toggleRowSelected(group.key, row.id)}
                                disabled={deleting}
                                aria-label={`Select duplicate row ${row.id}`}
                              />
                            </td>
                            <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
                              <code style={{ fontSize: '11px', wordBreak: 'break-all' }}>{row.id}</code>
                            </td>
                            <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
                              {row.status != null && String(row.status).trim() !== ''
                                ? String(row.status).trim()
                                : '—'}
                            </td>
                            <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
                              {row.waybill ?? ''}
                            </td>
                            <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
                              {row.area ?? ''}
                            </td>
                            <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', verticalAlign: 'top', maxWidth: '220px' }}>
                              {row.directions ?? ''}
                            </td>
                            <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
                              {row.cashCollection ?? ''}
                            </td>
                            <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
                              {row.packageQuantity ?? ''}
                            </td>
                            <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', verticalAlign: 'top', maxWidth: '180px' }}>
                              {row.notes ?? ''}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DuplicatedPage;
